import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scissors, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface TaskItem {
  id: string;
  title: string;
  duration: number;
  day: string;
  is_splittable: boolean;
  parentId?: string;
}

interface ScissorKanbanProps {
  initialTasks: TaskItem[];
}

export function ScissorKanban({ initialTasks }: ScissorKanbanProps) {
  const [tasks, setTasks] = useState<TaskItem[]>(initialTasks);
  const [isScissorMode, setIsScissorMode] = useState(false);

  const handleTaskClick = (task: TaskItem) => {
    if (!isScissorMode || !task.is_splittable) return;

    // Split logic
    setTasks(prev => {
      // Remove the original
      const filtered = prev.filter(t => t.id !== task.id);

      const halfDuration = task.duration / 2;

      // Create two halves
      const child1: TaskItem = {
        ...task,
        id: `${task.id}-child1-${Date.now()}`,
        duration: halfDuration,
        parentId: task.id,
      };

      const child2: TaskItem = {
        ...task,
        id: `${task.id}-child2-${Date.now()}`,
        duration: task.duration - halfDuration,
        parentId: task.id,
      };

      return [...filtered, child1, child2];
    });
  };

  const handleUnsplit = (e: React.MouseEvent, childTask: TaskItem) => {
    e.stopPropagation();
    const parentId = childTask.parentId;
    if (!parentId) return;

    setTasks(prev => {
      // Find all siblings
      const siblings = prev.filter(t => t.parentId === parentId);
      const otherTasks = prev.filter(t => t.parentId !== parentId);

      // Calculate total original duration
      const totalDuration = siblings.reduce((sum, t) => sum + t.duration, 0);

      // Recreate parent
      const parentTask: TaskItem = {
        id: parentId,
        title: childTask.title,
        duration: totalDuration,
        day: childTask.day,
        is_splittable: true,
      };

      return [...otherTasks, parentTask];
    });
  };

  return (
    <div className="flex flex-col gap-4 p-2 sm:gap-6 sm:p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-bold sm:text-xl">Scissor Kanban</h2>
        <Button
          variant={isScissorMode ? 'destructive' : 'outline'}
          onClick={() => setIsScissorMode(!isScissorMode)}
          className="flex items-center gap-2"
          size="sm"
        >
          <Scissors className="h-4 w-4" />
          {isScissorMode ? 'Exit Scissor Mode' : 'Scissor Mode'}
        </Button>
      </div>

      <div
        className={`bg-accent/20 flex min-h-[300px] gap-2 overflow-x-auto rounded-xl p-2 sm:min-h-[400px] sm:gap-4 sm:p-6 ${isScissorMode ? 'cursor-crosshair' : ''}`}
      >
        <AnimatePresence>
          {tasks.map(task => (
            <motion.div
              key={task.id}
              layout
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              whileHover={!isScissorMode ? { scale: 1.05 } : { scale: 1.02 }}
              whileTap={!isScissorMode ? { scale: 0.95 } : { scale: 0.98 }}
              drag={!isScissorMode}
              dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
              dragElastic={0.1}
              onClick={() => handleTaskClick(task)}
              className={`relative flex h-28 w-40 flex-shrink-0 flex-col justify-between rounded-lg border p-2 shadow-sm transition-colors sm:h-32 sm:w-48 sm:p-4 ${isScissorMode && task.is_splittable ? 'hover:border-destructive hover:bg-destructive/10 border-dashed' : 'bg-card border-border'} ${!task.is_splittable && isScissorMode ? 'opacity-50 grayscale' : ''} `}
            >
              <div>
                <h3 className="truncate text-sm font-semibold sm:text-base">{task.title}</h3>
                <p className="text-muted-foreground text-xs sm:text-sm">{task.duration} min</p>
              </div>

              {task.parentId && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute bottom-1 right-1 h-6 px-1.5 text-xs sm:bottom-2 sm:right-2 sm:h-7 sm:px-2"
                  onClick={e => handleUnsplit(e, task)}
                >
                  <Undo2 className="mr-0.5 h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Unsplit</span>
                </Button>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
